import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Image,
  Loader2,
  MessageSquare,
  RefreshCw,
  Search,
  User,
  XCircle,
  Zap
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';
import { useAuth } from '../../contexts/AuthContext';
import { TopupOrderService } from '../../services/topupOrderService';
import { TopupTemplateService } from '../../services/topupTemplateService';
import { ProductService } from '../../services/productService';
import TopupValidationModal from './TopupValidationModal';
import TopupOrderConversation from '../topup/TopupOrderConversation';

const formatCurrency = (amount) => {
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(amount || 0))} DA`;
};

const formatDate = (value) => {
  if (!value) return 'N/A';

  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizeText = (value) => String(value || '').trim();

const resolveProductImageUrl = (product = {}) => {
  return normalizeText(product?.detailImageUrl) ||
    normalizeText(product?.imageUrl) ||
    normalizeText(product?.coverImageUrl) ||
    '';
};

const isWithinQuickRange = (value, range) => {
  if (range === 'all' || !value) {
    return true;
  }

  const targetDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(targetDate.getTime())) {
    return false;
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  const diffInDays = Math.floor((startOfToday - startOfTarget) / (1000 * 60 * 60 * 24));

  if (range === 'today') {
    return diffInDays === 0;
  }

  if (range === 'yesterday') {
    return diffInDays === 1;
  }

  if (range === '7d') {
    return diffInDays >= 0 && diffInDays < 7;
  }

  if (range === '30d') {
    return diffInDays >= 0 && diffInDays < 30;
  }

  return true;
};

const QUICK_FILTERS = [
  { id: 'all', label: 'Tous' },
  { id: 'today', label: 'Aujourd hui' },
  { id: 'yesterday', label: 'Hier' },
  { id: '7d', label: '7 jours' },
  { id: '30d', label: '30 jours' }
];

const ORDERS_PER_PAGE = 10;

const getStatusClasses = (status) => {
  if (status === TopupOrderService.STATUSES.COMPLETED) {
    return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  }

  if (status === TopupOrderService.STATUSES.IN_PROGRESS) {
    return 'bg-blue-50 text-blue-700 border border-blue-200';
  }

  if (status === TopupOrderService.STATUSES.REFUSED) {
    return 'bg-red-50 text-red-700 border border-red-200';
  }

  return 'bg-amber-50 text-amber-700 border border-amber-200';
};

const TopupOrdersAdmin = () => {
  const { addNotification } = useNotification();
  const { currentUser, userProfile, isAdmin, isOperator } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [quickRange, setQuickRange] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [submissionFilter, setSubmissionFilter] = useState('all');
  const [resellerFilter, setResellerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState('');
  const [copiedFieldKey, setCopiedFieldKey] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [productImageMap, setProductImageMap] = useState({});
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [validationModal, setValidationModal] = useState({ isOpen: false, order: null });
  const highlightedOrderId = String(searchParams.get('orderId') || '').trim();

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const [nextOrders, products] = await Promise.all([
        TopupOrderService.getAllOrders(),
        ProductService.getAllProducts()
      ]);
      const nextProductImageMap = products.reduce((result, product) => {
        const imageUrl = resolveProductImageUrl(product);
        if (product?.id && imageUrl) {
          result[product.id] = imageUrl;
        }

        return result;
      }, {});

      setOrders(nextOrders);
      setProductImageMap(nextProductImageMap);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes Topup:', error);
      addNotification('error', 'Impossible de charger les commandes Topup');
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const stats = useMemo(() => {
    return {
      total: orders.length,
      pending: orders.filter((order) => order.status === TopupOrderService.STATUSES.PENDING).length,
      inProgress: orders.filter((order) => order.status === TopupOrderService.STATUSES.IN_PROGRESS).length,
      completed: orders.filter((order) => order.status === TopupOrderService.STATUSES.COMPLETED).length
    };
  }, [orders]);

  const productOptions = useMemo(() => {
    return Array.from(new Set(
      orders
        .map((order) => normalizeText(order.productName))
        .filter(Boolean)
    )).sort((first, second) => first.localeCompare(second, 'fr'));
  }, [orders]);

  const resellerOptions = useMemo(() => {
    return Array.from(new Set(
      orders
        .map((order) => normalizeText(order.resellerName))
        .filter(Boolean)
    )).sort((first, second) => first.localeCompare(second, 'fr'));
  }, [orders]);

  const submissionOptions = useMemo(() => {
    return Array.from(new Set(
      orders
        .map((order) => normalizeText(order.submissionMode))
        .filter(Boolean)
    ));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return orders.filter((order) => {
      if (!isWithinQuickRange(order.createdAt, quickRange)) {
        return false;
      }

      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false;
      }

      if (productFilter !== 'all' && normalizeText(order.productName) !== productFilter) {
        return false;
      }

      if (submissionFilter !== 'all' && normalizeText(order.submissionMode) !== submissionFilter) {
        return false;
      }

      if (resellerFilter !== 'all' && normalizeText(order.resellerName) !== resellerFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        order.productName,
        order.categoryName,
        order.templateName,
        order.templateFamily,
        order.resellerName,
        order.resellerEmail,
        order.offerName,
        order.offerValue,
        order.assignedToName,
        order.processedByName
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [orders, productFilter, quickRange, resellerFilter, searchTerm, statusFilter, submissionFilter]);

  const isAdminView = isAdmin();
  const isOperatorView = isOperator();

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, filteredOrders.length]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedOrderId('');
  }, [productFilter, quickRange, resellerFilter, searchTerm, statusFilter, submissionFilter]);

  useEffect(() => {
    if (!highlightedOrderId || filteredOrders.length === 0) {
      return;
    }

    const targetIndex = filteredOrders.findIndex((order) => order.id === highlightedOrderId);
    if (targetIndex === -1) {
      return;
    }

    const targetPage = Math.floor(targetIndex / ORDERS_PER_PAGE) + 1;
    if (currentPage !== targetPage) {
      setCurrentPage(targetPage);
    }

    if (selectedOrderId !== highlightedOrderId) {
      setSelectedOrderId(highlightedOrderId);
    }
  }, [currentPage, filteredOrders, highlightedOrderId, selectedOrderId]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderId((currentOrderId) => (currentOrderId === orderId ? '' : orderId));
  };

  const handleClaimOrder = async (orderId) => {
    try {
      setActionLoadingId(orderId);
      const result = await TopupOrderService.claimOrder(orderId);
      addNotification('success', result?.message || 'Commande Topup prise en charge');
      await loadOrders();
    } catch (error) {
      console.error('Erreur lors de la prise en charge de la commande Topup:', error);
      addNotification('error', error.message || 'Impossible de prendre en charge la commande Topup');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleReleaseOrder = async (orderId) => {
    try {
      setActionLoadingId(orderId);
      const result = await TopupOrderService.releaseOrder(orderId);
      addNotification('success', result?.message || 'Commande Topup relachee');
      await loadOrders();
    } catch (error) {
      console.error('Erreur lors du relachement de la commande Topup:', error);
      addNotification('error', error.message || 'Impossible de relacher la commande Topup');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleStatusUpdate = async (orderId, status, adminNote = '', proofImageUrl = '') => {
    const nextAdminNote =
      status === TopupOrderService.STATUSES.REFUSED
        ? (adminNote || window.prompt('Raison du refus ?') || '')
        : adminNote;

    if (status === TopupOrderService.STATUSES.REFUSED && !nextAdminNote.trim()) {
      return;
    }

    try {
      setActionLoadingId(orderId);
      const result = await TopupOrderService.updateOrderStatus(orderId, {
        status,
        adminNote: nextAdminNote,
        processedBy: currentUser?.uid || '',
        proofImageUrl
      });

      addNotification(
        'success',
        result?.message || `Commande Topup ${TopupOrderService.getStatusLabel(status).toLowerCase()}`
      );

      if (nextAdminNote.trim() && currentUser?.uid) {
        try {
          await TopupOrderService.sendOrderMessage(orderId, {
            senderId: currentUser.uid,
            senderRole: String(userProfile?.role || '').trim() || (isAdminView ? 'admin' : 'operator'),
            senderName: TopupOrderService.resolveCanonicalSenderName(
              String(userProfile?.role || '').trim() || (isAdminView ? 'admin' : 'operator')
            ),
            text: nextAdminNote,
            messageType: status === TopupOrderService.STATUSES.COMPLETED
              ? TopupOrderService.MESSAGE_TYPES.PRODUCT_INFO
              : TopupOrderService.MESSAGE_TYPES.STATUS_UPDATE
          });
        } catch (messageError) {
          console.error('Erreur lors de l ajout du message staff Topup:', messageError);
          addNotification('error', 'Commande mise a jour, mais le message n a pas pu etre ajoute au chat');
        }
      }

      await loadOrders();
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise a jour de la commande Topup:', error);
      addNotification('error', error.message || 'Impossible de mettre a jour la commande Topup');
      return null;
    } finally {
      setActionLoadingId('');
    }
  };

  const openValidationModal = (order) => {
    setValidationModal({ isOpen: true, order });
  };

  const closeValidationModal = () => {
    setValidationModal({ isOpen: false, order: null });
  };

  const handleCopyFieldValue = async (orderId, label, value) => {
    const textToCopy = normalizeText(value);
    if (!textToCopy) {
      return;
    }

    const fieldKey = `${orderId}:${label}`;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopiedFieldKey(fieldKey);
      addNotification('success', `${label} copie`);
      window.setTimeout(() => {
        setCopiedFieldKey((currentKey) => (currentKey === fieldKey ? '' : currentKey));
      }, 1800);
    } catch (error) {
      console.error('Erreur lors de la copie du contenu client:', error);
      addNotification('error', 'Impossible de copier cette information');
    }
  };

  const buildOrderDetails = (order) => {
    const fieldEntries = Array.isArray(order?.formFields)
      ? order.formFields
          .map((field) => ({
            label: field.label || field.id,
            value: String(order.formData?.[field.id] || '').trim()
          }))
          .filter((entry) => entry.value)
      : [];

    const productName = normalizeText(order?.productName) || 'Service Topup';
    const categoryName = normalizeText(order?.categoryName) || 'Topup';
    const templateName = normalizeText(order?.templateName);
    const templateFamily = normalizeText(order?.templateFamily);
    const offerName = normalizeText(order?.offerName);
    const offerValue = normalizeText(order?.offerValue);
    const fallbackProductImageUrl = normalizeText(productImageMap[order?.productId]);
    const productImageUrl = normalizeText(order?.productImageUrl) || fallbackProductImageUrl;
    const primaryOfferLabel = offerName || offerValue || 'Offre non renseignee';
    const showOfferValue = Boolean(offerValue) && offerValue !== primaryOfferLabel;
    const serviceMeta = [
      { label: 'Categorie', value: categoryName },
      { label: 'Template', value: templateName || templateFamily || 'Template standard' },
      { label: 'Valeur livree', value: offerValue || offerName || 'Non renseignee' }
    ];
    const assignedTo = normalizeText(order?.assignedTo);
    const assignedToName = normalizeText(order?.assignedToName);
    const isInProgress = order?.status === TopupOrderService.STATUSES.IN_PROGRESS;
    const isFinalized =
      order?.status === TopupOrderService.STATUSES.COMPLETED ||
      order?.status === TopupOrderService.STATUSES.REFUSED;
    const isAssignedToCurrentUser = Boolean(currentUser?.uid) && assignedTo === currentUser.uid;
    const isAssignedToOtherUser = Boolean(assignedTo) && !isAssignedToCurrentUser;
    const canClaim = (
      order?.status === TopupOrderService.STATUSES.PENDING ||
      order?.status === TopupOrderService.STATUSES.IN_PROGRESS
    ) && !assignedTo;
    const canRelease = (
      order?.status === TopupOrderService.STATUSES.PENDING ||
      order?.status === TopupOrderService.STATUSES.IN_PROGRESS
    ) && Boolean(assignedTo) && (isAssignedToCurrentUser || isAdminView);
    const canProcess = (
      order?.status === TopupOrderService.STATUSES.PENDING ||
      order?.status === TopupOrderService.STATUSES.IN_PROGRESS
    ) && (isAdminView || isAssignedToCurrentUser);

    return {
      order,
      fieldEntries,
      productName,
      categoryName,
      templateName,
      templateFamily,
      offerName,
      offerValue,
      fallbackProductImageUrl,
      productImageUrl,
      primaryOfferLabel,
      showOfferValue,
      serviceMeta,
      assignedTo,
      assignedToName,
      isInProgress,
      isFinalized,
      isAssignedToCurrentUser,
      isAssignedToOtherUser,
      canClaim,
      canRelease,
      canProcess,
      isActionLoading: actionLoadingId === order?.id
    };
  };

  const renderOrderDetailPanel = (details, { embedded = false } = {}) => {
    const {
      order,
      fieldEntries,
      productName,
      categoryName,
      templateName,
      templateFamily,
      productImageUrl,
      primaryOfferLabel,
      showOfferValue,
      offerValue,
      serviceMeta,
      assignedToName,
      isInProgress,
      isAssignedToCurrentUser,
      isAssignedToOtherUser,
      canClaim,
      canRelease,
      canProcess,
      isActionLoading
    } = details;

    return (
      <div className={`rounded-[24px] border border-slate-200 bg-white ${embedded ? 'p-4 sm:p-5' : 'p-5 sm:p-6'} shadow-sm`}>
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                {productImageUrl && !imageLoadErrors[order.id] ? (
                  <img
                    src={productImageUrl}
                    alt={productName}
                    className="h-16 w-16 shrink-0 rounded-2xl border border-slate-200 bg-slate-100 object-cover"
                    onError={() => setImageLoadErrors((current) => ({ ...current, [order.id]: true }))}
                  />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-400">
                    <Zap className="h-7 w-7" />
                  </div>
                )}

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-2xl font-bold text-slate-900">{productName}</h2>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(order.status)}`}>
                      {TopupOrderService.getStatusLabel(order.status)}
                    </span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {TopupTemplateService.getSubmissionModeLabel(order.submissionMode)}
                    </span>
                    {assignedToName && isInProgress ? (
                      <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                        En traitement par {assignedToName}
                      </span>
                    ) : null}
                  </div>

                  <p className="mt-2 text-sm font-semibold text-slate-700">
                    Offre commandee: {primaryOfferLabel}
                    {showOfferValue ? ` (${offerValue})` : ''}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {serviceMeta.map((item) => (
                      <span
                        key={item.label}
                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700"
                      >
                        {item.label}: {item.value}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 sm:max-w-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Resume operateur</p>
                <p className="mt-1 text-sm font-medium text-blue-900">
                  Traiter {productName} avec l'offre {primaryOfferLabel}
                  {showOfferValue ? ` et la valeur ${offerValue}` : ''}.
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Revendeur</p>
                <div className="mt-2 flex items-start gap-2 text-sm text-slate-700">
                  <User className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-900">{order.resellerName}</p>
                    <p>{order.resellerEmail || 'Email non renseigne'}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Service commande</p>
                <p className="mt-2 text-lg font-bold text-slate-900">{productName}</p>
                <p className="mt-1 text-sm text-slate-500">
                  {categoryName}
                  {templateName || templateFamily ? ` · ${templateName || templateFamily}` : ''}
                </p>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Offre</p>
                <p className="mt-2 text-lg font-bold text-slate-900">{primaryOfferLabel}</p>
                <p className="mt-1 text-sm text-slate-500">
                  {showOfferValue ? `Valeur: ${offerValue} · ` : ''}{formatCurrency(order.price)}
                </p>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Canaux</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(order.notificationChannels || []).map((channel) => (
                    <span
                      key={channel}
                      className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"
                    >
                      {channel}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Creation</p>
                <div className="mt-2 flex items-start gap-2 text-sm text-slate-700">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <p>{formatDate(order.createdAt)}</p>
                    <p className="text-slate-500">Ref: {order.id}</p>
                  </div>
                </div>
              </div>
            </div>

            {fieldEntries.length > 0 ? (
              <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-slate-400" />
                  <p className="text-sm font-semibold text-slate-900">Informations fournies</p>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {fieldEntries.map((entry) => (
                    <button
                      key={entry.label}
                      type="button"
                      onClick={() => handleCopyFieldValue(order.id, entry.label, entry.value)}
                      className="rounded-xl border border-transparent bg-white px-4 py-3 text-left transition hover:border-blue-200 hover:bg-blue-50/40"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                            {entry.label}
                          </p>
                          <p className="mt-1 break-words text-sm font-medium text-slate-800">
                            {entry.value}
                          </p>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                          {copiedFieldKey === `${order.id}:${entry.label}` ? (
                            <>
                              <Check className="h-3.5 w-3.5" />
                              Copie
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5" />
                              Copier
                            </>
                          )}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {assignedToName && isInProgress ? (
              <div className={`mt-4 rounded-2xl px-4 py-3 ${
                isAssignedToCurrentUser
                  ? 'border border-blue-200 bg-blue-50'
                  : 'border border-purple-200 bg-purple-50'
              }`}>
                <p className={`text-xs font-semibold uppercase tracking-wide ${
                  isAssignedToCurrentUser ? 'text-blue-700' : 'text-purple-700'
                }`}>
                  Prise en charge
                </p>
                <p className={`mt-1 text-sm font-medium ${
                  isAssignedToCurrentUser ? 'text-blue-900' : 'text-purple-900'
                }`}>
                  {isAssignedToCurrentUser
                    ? 'Cette commande vous est attribuee.'
                    : `Cette commande est en cours de traitement par ${assignedToName}.`}
                </p>
                {order.assignedAt ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Attribution: {formatDate(order.assignedAt)}
                  </p>
                ) : null}
              </div>
            ) : null}

            {order.adminNote ? (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Note admin</p>
                <p className="mt-1 text-sm text-amber-900">{order.adminNote}</p>
              </div>
            ) : null}

            {order.status === TopupOrderService.STATUSES.COMPLETED && order.proofImageUrl ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-2">
                    <Image className="mt-0.5 h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Capture de preuve</p>
                      <p className="text-sm text-emerald-700">
                        Preuve envoyee au revendeur pour cette commande.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => window.open(order.proofImageUrl, '_blank', 'noopener,noreferrer')}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Ouvrir la capture
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex w-full shrink-0 flex-col gap-2 xl:w-52">
            {canClaim ? (
              <button
                type="button"
                onClick={() => handleClaimOrder(order.id)}
                disabled={isActionLoading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                Prendre en charge
              </button>
            ) : null}

            {canRelease ? (
              <button
                type="button"
                onClick={() => handleReleaseOrder(order.id)}
                disabled={isActionLoading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw className="h-4 w-4" />
                Liberer
              </button>
            ) : null}

            {canProcess ? (
              <>
                <button
                  type="button"
                  onClick={() => openValidationModal(order)}
                  disabled={isActionLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Marquer terminee
                </button>

                <button
                  type="button"
                  onClick={() => handleStatusUpdate(order.id, TopupOrderService.STATUSES.REFUSED)}
                  disabled={isActionLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <XCircle className="h-4 w-4" />
                  Refuser
                </button>
              </>
            ) : null}

            {isOperatorView && isAssignedToOtherUser ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                Cette commande est reservee a {assignedToName || 'un autre agent'}.
              </div>
            ) : null}
          </div>
        </div>

        <TopupOrderConversation
          order={order}
          currentUser={currentUser}
          userProfile={userProfile}
          addNotification={addNotification}
          isStaff
          canSendMessages={isAdminView || isAssignedToCurrentUser}
          className="mt-5"
        />
      </div>
    );
  };

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
    return filteredOrders.slice(startIndex, startIndex + ORDERS_PER_PAGE);
  }, [currentPage, filteredOrders]);

  useEffect(() => {
    if (paginatedOrders.length === 0) {
      if (selectedOrderId) {
        setSelectedOrderId('');
      }
      return;
    }

    const hasSelectedOrder = paginatedOrders.some((order) => order.id === selectedOrderId);
    if (!hasSelectedOrder && selectedOrderId) {
      setSelectedOrderId('');
    }
  }, [paginatedOrders, selectedOrderId]);

  useEffect(() => {
    if (!highlightedOrderId || selectedOrderId !== highlightedOrderId) {
      return;
    }

    const desktopRow = document.getElementById(`topup-order-row-${highlightedOrderId}`);
    const mobileCard = document.getElementById(`topup-order-card-${highlightedOrderId}`);
    const targetElement = desktopRow || mobileCard;

    if (targetElement) {
      window.requestAnimationFrame(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [highlightedOrderId, selectedOrderId, currentPage]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 'ellipsis', totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages];
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="space-y-4">
        <div className="flex flex-col gap-4 rounded-[28px] border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-[28px] font-bold tracking-tight text-slate-900">
                {isOperatorView ? 'Commandes recues' : 'Commandes recues'}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Gerez et suivez toutes les commandes Topup passees par les revendeurs.
              </p>
            </div>

            <button
              type="button"
              onClick={loadOrders}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {QUICK_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setQuickRange(filter.id)}
                className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                  quickRange === filter.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[180px_180px_180px_180px_minmax(0,1fr)]">
            <select
              value={productFilter}
              onChange={(event) => setProductFilter(event.target.value)}
              className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-blue-400"
            >
              <option value="all">Tous les produits</option>
              {productOptions.map((productName) => (
                <option key={productName} value={productName}>{productName}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-blue-400"
            >
              <option value="all">Tous les statuts</option>
              <option value={TopupOrderService.STATUSES.PENDING}>En attente</option>
              <option value={TopupOrderService.STATUSES.IN_PROGRESS}>En cours</option>
              <option value={TopupOrderService.STATUSES.COMPLETED}>Terminees</option>
              <option value={TopupOrderService.STATUSES.REFUSED}>Refusees</option>
            </select>

            <select
              value={submissionFilter}
              onChange={(event) => setSubmissionFilter(event.target.value)}
              className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-blue-400"
            >
              <option value="all">Toutes les livraisons</option>
              {submissionOptions.map((submissionMode) => (
                <option key={submissionMode} value={submissionMode}>
                  {TopupTemplateService.getSubmissionModeLabel(submissionMode)}
                </option>
              ))}
            </select>

            <select
              value={resellerFilter}
              onChange={(event) => setResellerFilter(event.target.value)}
              className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-blue-400"
            >
              <option value="all">Tous les revendeurs</option>
              {resellerOptions.map((resellerName) => (
                <option key={resellerName} value={resellerName}>{resellerName}</option>
              ))}
            </select>

            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Par produit, commande ou revendeur"
                className="h-11 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-blue-400"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            <span>{stats.total} commandes au total</span>
            <span>{stats.pending} en attente</span>
            <span>{stats.inProgress} en cours</span>
            <span>{stats.completed} terminees</span>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white shadow-sm">
          {filteredOrders.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-base font-semibold text-slate-800">Aucune commande Topup</p>
              <p className="mt-2 text-sm text-slate-500">
                Les nouvelles commandes apparaitront ici des qu un revendeur envoie une demande.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3 p-4 md:hidden">
                {paginatedOrders.map((order) => {
                  const details = buildOrderDetails(order);
                  const isSelected = selectedOrderId === order.id;

                  return (
                    <div key={order.id} id={`topup-order-card-${order.id}`} className="space-y-3">
                      <button
                        type="button"
                        onClick={() => toggleOrderSelection(order.id)}
                        className={`w-full rounded-[22px] border px-4 py-4 text-left transition ${
                          isSelected
                            ? 'border-blue-300 bg-blue-50/70'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {details.productImageUrl && !imageLoadErrors[`list-${order.id}`] ? (
                            <img
                              src={details.productImageUrl}
                              alt={details.productName}
                              className="h-12 w-12 shrink-0 rounded-2xl border border-slate-200 object-cover"
                              onError={() => setImageLoadErrors((current) => ({ ...current, [`list-${order.id}`]: true }))}
                            />
                          ) : (
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-400">
                              <Zap className="h-5 w-5" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="truncate text-sm font-semibold text-slate-900">{details.productName}</h3>
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${getStatusClasses(order.status)}`}>
                                {TopupOrderService.getStatusLabel(order.status)}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-slate-500">{details.primaryOfferLabel}</p>
                            <p className="mt-1 text-xs text-slate-400">{order.resellerName}</p>
                          </div>
                        </div>
                      </button>

                      {isSelected ? renderOrderDetailPanel(details, { embedded: true }) : null}
                    </div>
                  );
                })}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      <th className="px-5 py-4">Produit</th>
                      <th className="px-4 py-4">Commande</th>
                      <th className="px-4 py-4">Date</th>
                      <th className="px-4 py-4">Revendeur</th>
                      <th className="px-4 py-4">Offre</th>
                      <th className="px-4 py-4">Paiement</th>
                      <th className="px-4 py-4">Livraison</th>
                      <th className="px-4 py-4">Statut</th>
                      <th className="px-4 py-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order) => {
                      const details = buildOrderDetails(order);
                      const isSelected = selectedOrderId === order.id;

                      return (
                        <React.Fragment key={order.id}>
                          <tr
                            id={`topup-order-row-${order.id}`}
                            onClick={() => toggleOrderSelection(order.id)}
                            className={`cursor-pointer border-b border-slate-100 transition ${
                              isSelected
                                ? 'bg-blue-50/60'
                                : 'bg-white hover:bg-slate-50/80'
                            }`}
                          >
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                {details.productImageUrl && !imageLoadErrors[`list-${order.id}`] ? (
                                  <img
                                    src={details.productImageUrl}
                                    alt={details.productName}
                                    className="h-11 w-11 shrink-0 rounded-2xl border border-slate-200 object-cover"
                                    onError={() => setImageLoadErrors((current) => ({ ...current, [`list-${order.id}`]: true }))}
                                  />
                                ) : (
                                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-400">
                                    <Zap className="h-4 w-4" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-900">{details.productName}</p>
                                  <p className="mt-1 truncate text-xs text-slate-400">{details.categoryName}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm font-semibold text-slate-900">#{String(order.id).slice(0, 8).toUpperCase()}</p>
                              <p className="mt-1 text-xs text-slate-400">{order.id}</p>
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-600">{formatDate(order.createdAt)}</td>
                            <td className="px-4 py-4">
                              <p className="text-sm font-medium text-slate-900">{order.resellerName}</p>
                              <p className="mt-1 text-xs text-slate-400">{order.resellerEmail || 'Email non renseigne'}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm font-medium text-slate-900">{details.primaryOfferLabel}</p>
                              <p className="mt-1 text-xs text-slate-400">{details.offerValue || 'Valeur non renseignee'}</p>
                            </td>
                            <td className="px-4 py-4 text-sm font-semibold text-slate-900">{formatCurrency(order.price)}</td>
                            <td className="px-4 py-4">
                              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                                {TopupTemplateService.getSubmissionModeLabel(order.submissionMode)}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-wrap gap-2">
                                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(order.status)}`}>
                                  {TopupOrderService.getStatusLabel(order.status)}
                                </span>
                                {details.assignedToName && details.isInProgress ? (
                                  <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                                    {details.assignedToName}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleOrderSelection(order.id);
                                }}
                                className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                              >
                                Ouvrir
                              </button>
                            </td>
                          </tr>
                          {isSelected ? (
                            <tr className="border-b border-slate-100 bg-slate-50/60">
                              <td colSpan={9} className="px-4 py-4">
                                {renderOrderDetailPanel(details, { embedded: true })}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-4 border-t border-slate-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-slate-500">
                  Affichage de {paginatedOrders.length === 0 ? 0 : ((currentPage - 1) * ORDERS_PER_PAGE) + 1}
                  {' '}a{' '}
                  {Math.min(currentPage * ORDERS_PER_PAGE, filteredOrders.length)}
                  {' '}sur {filteredOrders.length} commandes
                </p>

                <div className="flex items-center gap-2 self-end">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="flex h-10 min-w-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {'<'}
                  </button>

                  {paginationItems.map((item, index) => (
                    item === 'ellipsis' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-sm font-semibold text-slate-400">...</span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setCurrentPage(item)}
                        className={`flex h-10 min-w-10 items-center justify-center rounded-xl px-3 text-sm font-semibold transition ${
                          currentPage === item
                            ? 'bg-blue-600 text-white'
                            : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {item}
                      </button>
                    )
                  ))}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="flex h-10 min-w-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {'>'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <TopupValidationModal
        isOpen={validationModal.isOpen}
        onClose={closeValidationModal}
        onValidate={handleStatusUpdate}
        order={validationModal.order}
      />
    </div>
  );
};

export default TopupOrdersAdmin;
